import { useMutation, useQueryClient } from "@tanstack/react-query";
import { serviceTicketService } from "./service-ticket.services";
import { SERVICE_TICKET_KEYS } from "./service-ticket.keys";
import { BOOKING_KEYS } from "../booking";
import { STAFF_AVAILABILITY_KEYS } from "../staff/staff-availability.queries";

export const useCreateServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.createServiceTicket,
    onSuccess: (data, variables) => {
      // Invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });

      // Invalidate booking queries to update booking status (since adding a service ticket can change booking status)
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // CRITICAL: When a new service ticket is added to a completed booking,
      // the booking status should automatically revert to "scheduled"
      const bookingId = data?.jobTicket?.id || variables.jobTicketId;
      if (bookingId) {
        console.log('Invalidating booking after new service ticket added:', bookingId);
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });

        // Aggressively refetch the booking to ensure status updates immediately
        qc.refetchQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }
    },
  });
};

export const useUpdateServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.updateServiceTicket,
    onSuccess: (data, variables) => {
      // Invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });

      // Invalidate specific service ticket
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(variables.id),
      });

      // Invalidate booking queries to update booking status (since updating a service ticket status can change booking status)
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // Invalidate specific booking - try multiple possible sources for the booking ID
      const bookingId = data?.jobTicket?.id || variables.jobTicketId;
      if (bookingId) {
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }
    },
  });
};

export const useCompleteServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.completeServiceTicket,
    onSuccess: (data, variables) => {
      // Invalidate specific service ticket queries
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(variables.id),
      });

      // Invalidate service ticket lists
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET, "list"],
      });

      // Invalidate my service tickets (for assignments page)
      qc.invalidateQueries({
        queryKey: [`${SERVICE_TICKET_KEYS.SERVICE_TICKET}/myServices`],
      });

      // Invalidate ALL booking queries more aggressively
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKING],
      });

      // Invalidate specific booking queries that matter for status updates
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_MY_BOOKINGS(),
      });

      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_ALL_MY_RELATED_BOOKINGS(),
      });

      // Invalidate upcoming bookings with services (for assignments)
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_UPCOMING_BOOKINGS_INCLUDING_SERVICES(),
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // CRITICAL: Invalidate the specific booking that contains this completed service ticket
      // This ensures the booking header status updates immediately
      const bookingId = data?.jobTicket?.id;
      if (bookingId) {
        console.log('Invalidating specific booking:', bookingId);
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });

        // Also refetch it immediately to ensure fresh data
        qc.refetchQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }

      // Force a complete refresh by invalidating the entire query cache for bookings
      qc.refetchQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });
    },
  });
};

export const useDeleteServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.deleteServiceTicket,
    onSuccess: (data, deletedServiceTicketId) => {
      // Invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });

      // Invalidate booking queries to update booking status (since removing a service ticket can change booking status)
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // Invalidate specific booking if available in the response
      const bookingId = data?.jobTicket?.id;
      if (bookingId) {
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }

      // Note: If we don't have the jobTicketId in the delete response, the broad invalidation above
      // will ensure booking status updates when service tickets are deleted
    },
  });
};

export const useImportServiceTickets = () => {
  return useMutation({
    mutationFn: serviceTicketService.importServiceTickets,
  });
};

export const useCalculateEstimate = () => {
  return useMutation({
    mutationFn: serviceTicketService.calculateEstimate,
  });
};